import React, { useEffect, useState } from "react";
import axios from "axios";
import { Gift, Trash2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const fmtDt = (iso) => { try { return new Date(iso).toLocaleString("ar-IQ"); } catch { return iso; } };

// "كراتين الهدايا" — Free cartons received from suppliers.
// Adds to stock + entire price is counted as pure profit (cost = 0).
const GiftsTab = () => {
  const [products, setProducts] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null); // product to gift
  const [qty, setQty] = useState("");
  const [supplier, setSupplier] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, g] = await Promise.all([
        axios.get(`${API}/admin/products`),
        axios.get(`${API}/admin/gifts`),
      ]);
      setProducts(p.data || []);
      setGifts(g.data.items || []);
      setTotalValue(Number(g.data.total_value || 0));
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };

  const save = async () => {
    const q = parseInt(qty);
    if (!q || q <= 0) { toast.error("أدخل كمية صحيحة"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/gifts`, {
        product_id: open.id, quantity: q, supplier_name: supplier,
      });
      toast.success("تم تسجيل الهدية وإضافة الكميّة للمخزن");
      setOpen(null); setQty(""); setSupplier("");
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل"); }
    finally { setBusy(false); }
  };

  const del = async (gift) => {
    const ok = await confirmDialog(
      `حذف هدية ${gift.quantity} كرتون من ${gift.product_name}؟\nسيُخصم من المخزن.`,
      { force: true, tone: "danger", confirmLabel: "نعم احذف" }
    );
    if (!ok) return;
    try { await axios.delete(`${API}/admin/gifts/${gift.id}`); toast.success("حُذف"); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "فشل"); }
  };

  const filtered = products.filter((p) =>
    !search.trim() || (p.name_ar || p.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" dir="rtl">
      <div className="bg-white rounded-2xl border border-[#E4EBE6] p-4">
        <div className="flex items-center gap-2 mb-2">
          <Gift className="w-5 h-5 text-[#F59E0B]" />
          <h2 className="font-black text-lg text-[#1E3A5F]">كراتين الهدايا</h2>
        </div>
        <p className="text-xs text-[#5C6E64] mb-3">سجّل أي كرتون هدية تستلمه من مجهّز. الكميّة تنضاف للمخزن وقيمتها الكاملة تنضاف للأرباح تلقائياً.</p>
        <div className="bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm text-[#92400E] font-bold">إجمالي قيمة الهدايا المضافة للأرباح</span>
          <span className="font-black text-lg text-[#F59E0B]" dir="ltr">{fmt(totalValue)} د.ع</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E4EBE6] p-4">
        <p className="font-bold text-[#1E3A5F] mb-2 flex items-center gap-1.5"><Plus className="w-4 h-4" /> تسجيل هدية جديدة</p>
        <div className="relative mb-3">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="ابحث عن منتج لتسجيله كهدية..."
            data-testid="gift-product-search"
            className="w-full pr-9 pl-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#F59E0B] text-sm"
          />
        </div>
        {loading ? (
          <div className="text-center py-4 text-[#5C6E64]">جاري التحميل...</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-72 overflow-y-auto">
            {filtered.slice(0, 60).map((p) => (
              <button
                type="button" key={p.id}
                onClick={() => { setOpen(p); setQty(""); setSupplier(""); }}
                data-testid={`gift-pick-${p.id}`}
                className="bg-white border border-[#E4EBE6] rounded-xl p-2 text-right hover:border-[#F59E0B] transition-colors text-sm"
              >
                <p className="font-bold text-[#1E3A5F] truncate">{p.name_ar || p.name}</p>
                <p className="text-xs text-[#5C6E64]" dir="ltr">{fmt(p.price)} د.ع</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E4EBE6] overflow-hidden">
        <div className="bg-[#F3F6F4] px-4 py-2 font-black text-[#1E3A5F] text-sm">سجل الهدايا المستلمة ({gifts.length})</div>
        {gifts.length === 0 ? (
          <p className="text-center py-6 text-[#5C6E64] text-sm">لم تُسجَّل أي هدية بعد</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] text-[#5C6E64] text-xs">
              <tr>
                <th className="px-3 py-2 text-right">المنتج</th>
                <th className="px-3 py-2 text-center">الكمية</th>
                <th className="px-3 py-2 text-center">القيمة</th>
                <th className="px-3 py-2 text-right">المجهّز</th>
                <th className="px-3 py-2 text-right">التاريخ</th>
                <th className="px-3 py-2 text-center">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {gifts.map((g) => (
                <tr key={g.id} className="border-t border-[#E4EBE6]" data-testid={`gift-row-${g.id}`}>
                  <td className="px-3 py-2 text-right font-bold">{g.product_name}</td>
                  <td className="px-3 py-2 text-center font-black text-[#F59E0B]">{g.quantity}</td>
                  <td className="px-3 py-2 text-center font-bold" dir="ltr">{fmt(g.total_value)} د.ع</td>
                  <td className="px-3 py-2 text-right text-xs">{g.supplier_name || "—"}</td>
                  <td className="px-3 py-2 text-right text-xs text-[#5C6E64]">{fmtDt(g.created_at)}</td>
                  <td className="px-3 py-2 text-center">
                    <button type="button" onClick={() => del(g)} data-testid={`gift-del-${g.id}`} className="text-[#DC2626] hover:bg-[#FEE2E2] p-1 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[160] bg-black/60 flex items-center justify-center p-3" dir="rtl" onClick={() => setOpen(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <p className="font-black text-[#1E3A5F]">تسجيل هدية: <span className="text-[#F59E0B]">{open.name_ar || open.name}</span></p>
            <p className="text-xs text-[#5C6E64]">سعر البيع الحالي: <span className="font-bold" dir="ltr">{fmt(open.price)} د.ع</span></p>
            <input
              type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)}
              placeholder="الكمية (كم كرتون هدية؟)"
              data-testid="gift-qty-input" autoFocus
              className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]"
            />
            <input
              type="text" value={supplier} onChange={(e) => setSupplier(e.target.value)}
              placeholder="اسم المجهّز / الشركة (اختياري)"
              data-testid="gift-supplier-input"
              className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#F59E0B] text-sm text-right"
            />
            {qty > 0 && (
              <p className="text-xs font-bold text-center text-[#15803D]" dir="ltr">
                ستضاف {fmt(Number(qty) * Number(open.price))} د.ع للأرباح
              </p>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={save} disabled={busy} data-testid="gift-save-btn" className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] disabled:opacity-50 text-white font-bold py-2 rounded-xl">
                {busy ? "جاري الحفظ..." : "حفظ الهدية"}
              </button>
              <button type="button" onClick={() => setOpen(null)} className="bg-[#F3F6F4] text-[#5C6E64] font-bold px-4 rounded-xl">إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftsTab;
